import React, { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import auth from "../firebase.init";

const ResetPassword = () => {
  const [email, setEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const getErrorMessage = (error) => {
    if (!error) return "";

    switch (error.code) {
      case "auth/user-not-found":
        return "No account found with this email address.";

      case "auth/invalid-email":
        return "Please enter a valid email address.";

      case "auth/network-request-failed":
        return "Network error. Please check your connection and try again.";

      case "auth/too-many-requests":
        return "Too many requests. Please try again later.";

      default:
        return "Unable to send password reset email. Please try again.";
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    setError("");

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email.trim());

      setResetSent(true);
      setError("");
    } catch (error) {
      console.error("Password reset error:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleTryAgain = () => {
    setResetSent(false);
    setError("");
  };

  return (
    <>
      <Helmet>
        <title>Reset Password - Recover Your Account</title>

        <meta
          name="description"
          content="Reset your password and securely regain access to your account."
        />
      </Helmet>

      <section className="modern-reset-page">
        <div className="reset-container">
          <div className="reset-card">

            {/* Header */}
            <div className="reset-header">

              {/* Close Button */}
              <div className="close-button">
                <Link to="/" className="close-link">
                  <i className="fas fa-times"></i>
                </Link>
              </div>

              {/* Logo */}
              <div className="logo-section">
                <Link to="/">
                  <img
                    src="/images/resource/login-page-logo.png"
                    alt="Logo"
                    className="reset-logo"
                  />
                </Link>
              </div>

              {/* Heading */}
              <div className="reset-welcome-section">
                <div className="reset-icon">
                  <i className="fas fa-lock"></i>
                </div>

                <h1>Reset Your Password</h1>

                <p>
                  Enter your email address and we'll send you a link to
                  reset your password.
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="reset-content">

              {/* SUCCESS STATE */}
              {resetSent ? (
                <div className="reset-success">

                  <div className="success-icon">
                    <i className="fas fa-check"></i>
                  </div>

                  <h2>Email Sent Successfully!</h2>

                  <p className="success-description">
                    We've sent password reset instructions to:
                  </p>

                  <div className="email-display">
                    <i className="fas fa-envelope"></i>
                    <span>{email}</span>
                  </div>

                  <div className="success-help">
                    <i className="fas fa-info-circle"></i>

                    <p>
                      Didn't receive the email? Please check your
                      spam or junk folder.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleTryAgain}
                    className="try-again-btn"
                  >
                    <i className="fas fa-redo"></i>
                    Try Another Email
                  </button>

                  <Link
                    to="/login"
                    className="back-login-btn"
                  >
                    <i className="fas fa-arrow-left"></i>
                    Back to Login
                  </Link>
                </div>
              ) : (
                <>
                  {/* ERROR */}
                  {error && (
                    <div className="reset-error">
                      <div className="error-icon">
                        <i className="fas fa-exclamation-circle"></i>
                      </div>

                      <div>
                        <strong>Something went wrong</strong>
                        <p>{error}</p>
                      </div>
                    </div>
                  )}

                  {/* FORM */}
                  <form
                    onSubmit={handleResetPassword}
                    className="reset-form"
                  >
                    <div className="form-group">
                      <label htmlFor="reset-email">
                        Email Address
                      </label>

                      <div className="input-wrapper">
                        <i className="fas fa-envelope"></i>

                        <input
                          type="email"
                          id="reset-email"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (error) setError("");
                          }}
                          placeholder="Enter your email"
                          autoComplete="email"
                          required
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="reset-submit-btn"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="button-spinner"></span>
                          Sending Reset Link...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i>
                          Send Reset Link
                        </>
                      )}
                    </button>
                  </form>

                  {/* Back Login */}
                  <div className="back-login">
                    <Link to="/login">
                      <i className="fas fa-arrow-left"></i>
                      Back to Login
                    </Link>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="reset-footer">
              <p>
                Remember your password?{" "}
                <Link to="/login">
                  Sign in to your account
                </Link>
              </p>
            </div>

          </div>
        </div>

        <style>{`
          /* =========================================
             RESET PASSWORD PAGE
          ========================================= */

          .modern-reset-page {
            min-height: 100vh;
            background-image: url('/images/shapes/login-page-shape-1.png');
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;

            display: flex;
            align-items: center;
            justify-content: center;

            padding: 30px 20px;

            position: relative;
            overflow: hidden;
          }

          .modern-reset-page::before {
            content: "";
            position: absolute;

            top: 0;
            left: 0;
            right: 0;
            bottom: 0;

            background: rgba(0, 0, 0, 0.3);

            backdrop-filter: blur(1px);
          }


          /* =========================================
             CONTAINER
          ========================================= */

          .reset-container {
            position: relative;
            z-index: 1;

            width: 100%;
            max-width: 500px;
          }


          /* =========================================
             CARD
          ========================================= */

          .reset-card {
            background: rgba(255, 255, 255, 0.96);

            backdrop-filter: blur(10px);

            border-radius: 20px;

            box-shadow:
              0 20px 40px rgba(0, 0, 0, 0.12);

            overflow: hidden;

            border: 1px solid rgba(255, 255, 255, 0.3);
          }


          /* =========================================
             HEADER
          ========================================= */

          .reset-header {
            padding: 40px 40px 30px;

            text-align: center;

            position: relative;
          }


          /* =========================================
             CLOSE BUTTON
          ========================================= */

          .close-button {
            position: absolute;

            top: 20px;
            right: 20px;
          }

          .close-link {
            width: 40px;
            height: 40px;

            display: flex;
            align-items: center;
            justify-content: center;

            background: rgba(0, 0, 0, 0.07);

            border-radius: 50%;

            color: #666;

            text-decoration: none;

            transition: all 0.3s ease;
          }

          .close-link:hover {
            background: rgba(102, 126, 234, 0.12);

            color: #667eea;

            transform: rotate(90deg);
          }


          /* =========================================
             LOGO
          ========================================= */

          .logo-section {
            margin-bottom: 25px;
          }

          .reset-logo {
            max-width: 150px;
            max-height: 60px;

            width: auto;
            height: auto;

            object-fit: contain;
          }


          /* =========================================
             RESET ICON
          ========================================= */

          .reset-icon {
            width: 64px;
            height: 64px;

            margin: 0 auto 18px;

            display: flex;
            align-items: center;
            justify-content: center;

            border-radius: 50%;

            background: rgba(102, 126, 234, 0.1);

            color: #667eea;

            font-size: 25px;
          }


          /* =========================================
             HEADING
          ========================================= */

          .reset-welcome-section h1 {
            font-size: 28px;

            font-weight: 700;

            color: #333;

            margin: 0 0 10px;
          }

          .reset-welcome-section p {
            font-size: 15px;

            line-height: 1.6;

            color: #666;

            max-width: 380px;

            margin: 0 auto;
          }


          /* =========================================
             CONTENT
          ========================================= */

          .reset-content {
            padding: 0 40px 30px;
          }


          /* =========================================
             ERROR MESSAGE
          ========================================= */

          .reset-error {
            background: #fff1f1;

            border: 1px solid #ffd0d0;

            border-radius: 12px;

            padding: 14px 16px;

            margin-bottom: 22px;

            display: flex;

            align-items: flex-start;

            gap: 12px;
          }

          .error-icon {
            color: #d32f2f;

            font-size: 18px;

            margin-top: 1px;
          }

          .reset-error strong {
            display: block;

            color: #c62828;

            font-size: 14px;

            margin-bottom: 3px;
          }

          .reset-error p {
            color: #d32f2f;

            margin: 0;

            font-size: 13px;

            line-height: 1.5;
          }


          /* =========================================
             FORM
          ========================================= */

          .reset-form {
            margin-bottom: 20px;
          }

          .form-group {
            margin-bottom: 20px;
          }

          .form-group label {
            display: block;

            margin-bottom: 8px;

            font-weight: 500;

            color: #333;

            font-size: 14px;
          }


          /* =========================================
             INPUT
          ========================================= */

          .input-wrapper {
            position: relative;
          }

          .input-wrapper > i {
            position: absolute;

            left: 17px;

            top: 50%;

            transform: translateY(-50%);

            color: #999;

            font-size: 15px;

            pointer-events: none;

            transition: color 0.3s ease;
          }

          .input-wrapper input {
            width: 100%;

            height: 54px;

            padding: 0 16px 0 46px;

            border: 1px solid #e0e0e0;

            border-radius: 10px;

            font-size: 15px;

            color: #333;

            background: #fff;

            transition: all 0.3s ease;

            box-sizing: border-box;
          }

          .input-wrapper input::placeholder {
            color: #aaa;
          }

          .input-wrapper input:focus {
            outline: none;

            border-color: #667eea;

            box-shadow:
              0 0 0 3px rgba(102, 126, 234, 0.1);
          }

          .input-wrapper:focus-within > i {
            color: #667eea;
          }


          /* =========================================
             SUBMIT BUTTON
          ========================================= */

          .reset-submit-btn {
            width: 100%;

            height: 56px;

            background: #667eea;

            border: none;

            border-radius: 12px;

            color: #fff;

            font-size: 16px;

            font-weight: 600;

            cursor: pointer;

            transition: all 0.3s ease;

            display: flex;

            align-items: center;

            justify-content: center;

            gap: 9px;
          }

          .reset-submit-btn:hover {
            background: #5a67d8;

            transform: translateY(-2px);

            box-shadow:
              0 5px 15px rgba(102, 126, 234, 0.3);
          }

          .reset-submit-btn:active {
            transform: translateY(0);
          }

          .reset-submit-btn:disabled {
            background: #a0aec0;

            cursor: not-allowed;

            transform: none;

            box-shadow: none;
          }


          /* =========================================
             LOADING SPINNER
          ========================================= */

          .button-spinner {
            width: 17px;
            height: 17px;

            border: 2px solid rgba(255, 255, 255, 0.4);

            border-top-color: #fff;

            border-radius: 50%;

            animation: resetSpinner 0.7s linear infinite;
          }

          @keyframes resetSpinner {
            to {
              transform: rotate(360deg);
            }
          }


          /* =========================================
             BACK TO LOGIN
          ========================================= */

          .back-login {
            text-align: center;

            margin-top: 22px;
          }

          .back-login a {
            display: inline-flex;

            align-items: center;

            gap: 7px;

            color: #667eea;

            font-size: 14px;

            font-weight: 500;

            text-decoration: none;

            transition: all 0.3s ease;
          }

          .back-login a:hover {
            color: #5a67d8;

            transform: translateX(-2px);
          }


          /* =========================================
             SUCCESS STATE
          ========================================= */

          .reset-success {
            text-align: center;

            padding: 5px 0 5px;
          }

          .success-icon {
            width: 72px;
            height: 72px;

            margin: 0 auto 20px;

            display: flex;
            align-items: center;
            justify-content: center;

            background: #e8f7ee;

            color: #28a745;

            border-radius: 50%;

            font-size: 30px;
          }

          .reset-success h2 {
            font-size: 23px;

            font-weight: 700;

            color: #333;

            margin: 0 0 10px;
          }

          .success-description {
            color: #666;

            font-size: 14px;

            margin: 0 0 12px;
          }

          .email-display {
            display: flex;

            align-items: center;

            justify-content: center;

            gap: 8px;

            padding: 12px 15px;

            background: #f7f8fc;

            border: 1px solid #e6e8f0;

            border-radius: 10px;

            color: #4a4a4a;

            font-size: 14px;

            font-weight: 500;

            word-break: break-word;

            margin-bottom: 18px;
          }

          .email-display i {
            color: #667eea;

            flex-shrink: 0;
          }


          /* =========================================
             SUCCESS HELP
          ========================================= */

          .success-help {
            display: flex;

            align-items: flex-start;

            gap: 10px;

            text-align: left;

            background: rgba(102, 126, 234, 0.07);

            border-radius: 10px;

            padding: 13px 14px;

            margin-bottom: 20px;
          }

          .success-help i {
            color: #667eea;

            font-size: 15px;

            margin-top: 2px;
          }

          .success-help p {
            margin: 0;

            color: #666;

            font-size: 13px;

            line-height: 1.5;
          }


          /* =========================================
             TRY AGAIN
          ========================================= */

          .try-again-btn {
            width: 100%;

            height: 50px;

            background: #fff;

            border: 1px solid #667eea;

            border-radius: 10px;

            color: #667eea;

            font-size: 14px;

            font-weight: 600;

            cursor: pointer;

            display: flex;

            align-items: center;

            justify-content: center;

            gap: 8px;

            transition: all 0.3s ease;

            margin-bottom: 12px;
          }

          .try-again-btn:hover {
            background: rgba(102, 126, 234, 0.06);

            transform: translateY(-1px);
          }


          /* =========================================
             SUCCESS BACK LOGIN BUTTON
          ========================================= */

          .back-login-btn {
            width: 100%;

            height: 50px;

            background: #667eea;

            border-radius: 10px;

            color: #fff;

            text-decoration: none;

            font-size: 14px;

            font-weight: 600;

            display: flex;

            align-items: center;

            justify-content: center;

            gap: 8px;

            transition: all 0.3s ease;
          }

          .back-login-btn:hover {
            background: #5a67d8;

            color: #fff;

            transform: translateY(-1px);

            box-shadow:
              0 4px 12px rgba(102, 126, 234, 0.25);
          }


          /* =========================================
             FOOTER
          ========================================= */

          .reset-footer {
            padding: 25px 40px;

            background: rgba(0, 0, 0, 0.02);

            text-align: center;

            border-top: 1px solid rgba(0, 0, 0, 0.05);
          }

          .reset-footer p {
            margin: 0;

            color: #666;

            font-size: 14px;
          }

          .reset-footer a {
            color: #667eea;

            text-decoration: none;

            font-weight: 600;
          }

          .reset-footer a:hover {
            text-decoration: underline;
          }


          /* =========================================
             TABLET
          ========================================= */

          @media (max-width: 768px) {

            .modern-reset-page {
              padding: 20px 15px;
            }

            .reset-header {
              padding: 35px 30px 25px;
            }

            .reset-content {
              padding: 0 30px 25px;
            }

            .reset-footer {
              padding: 22px 30px;
            }

            .reset-welcome-section h1 {
              font-size: 25px;
            }
          }


          /* =========================================
             MOBILE
          ========================================= */

          @media (max-width: 480px) {

            .modern-reset-page {
              padding: 15px 10px;
            }

            .reset-card {
              border-radius: 16px;
            }

            .reset-header {
              padding: 30px 20px 22px;
            }

            .reset-content {
              padding: 0 20px 22px;
            }

            .reset-footer {
              padding: 18px 20px;
            }

            .reset-welcome-section h1 {
              font-size: 22px;
            }

            .reset-welcome-section p {
              font-size: 14px;
            }

            .reset-icon {
              width: 58px;
              height: 58px;

              font-size: 22px;
            }

            .reset-logo {
              max-width: 130px;
            }

            .close-button {
              top: 15px;
              right: 15px;
            }

            .close-link {
              width: 36px;
              height: 36px;
            }

            .input-wrapper input {
              height: 52px;
            }

            .reset-submit-btn {
              height: 52px;
              font-size: 15px;
            }

            .reset-success h2 {
              font-size: 20px;
            }

            .email-display {
              font-size: 13px;
            }
          }
        `}</style>
      </section>
    </>
  );
};

export default ResetPassword;